import { MenuItem, MenuStructure, RoutesStructure, RoutesStructureWithStringKeys } from '@app/types/routing.types';
import { useAuthContext } from '@context/auth/useAuthContext';
import getMenu from '@context/routing/utils/getMenu';
import { doctorPortalPaths, restrictMenuToDoctorPortal } from '@context/routing/utils/doctorPortalRoutes';
import { ReactNode, useCallback, useMemo } from 'react';
import { RoutingContext } from './RoutingContext';
import { generateRouteStructure, getAllPaths, getIndexRouteKey, getParentKey } from './utils/routerHelpers';
import RoutingTransformer from './utils/RoutingTransformer';

export const RoutingProvider = ({ children }: { children: ReactNode }) => {
  // const { allowIf, permissions, auth } = useAuthContext();
  // const { showFeature } = useFeatureContext();

  const { auth, user } = useAuthContext();

  // Billing module is restricted to true administrators. Roles that inherit
  // admin access but not billing (e.g. Office Administrator / 'ofa') are excluded.
  const canViewBilling = user.isAdmin;

  // Visit summary reports call an admin-only backend endpoint (list-all-summaries),
  // so both true admins and Office Administrators can see it.
  const canViewVisitSummaries = user.isAdmin || user.isOfficeAdmin;

  // Inventory is office data (not billing-restricted), so both true admins and
  // Office Administrators can manage it.
  const canManageInventory = user.isAdmin || user.isOfficeAdmin;

  // A doctor is not a narrower administrator: they get their own route tree, and
  // none of the staff one.
  const isDoctor = user.isDoctor;

  // Deciding a doctor's instruction is guarded server-side by the 'adm' realm
  // role, which the API layer also grants to the admin-inheriting Office
  // Administrator — so both see the queue, and nobody else.
  const canReviewDoctorInstructions = user.isAdmin || user.isOfficeAdmin;

  const RouteConstruct: RoutesStructure = useMemo(() => {
    return generateRouteStructure(
      canViewBilling,
      canViewVisitSummaries,
      canManageInventory,
      isDoctor,
      canReviewDoctorInstructions
    );
  }, [canViewBilling, canViewVisitSummaries, canManageInventory, isDoctor, canReviewDoctorInstructions]);

  const menuStructure: MenuStructure = useMemo(() => {
    const menu = getMenu(RouteConstruct); // or getMenu(RouteConstruct, showFeature);
    // Hiding the ROUTES is not enough: several entries in getMenu carry a literal
    // `show: true` and never consult a route, so they survived the restriction and
    // sat in a doctor's sidebar pointing at pages they cannot open.
    return isDoctor ? restrictMenuToDoctorPortal(menu, doctorPortalPaths(RouteConstruct)) : menu;
  }, [RouteConstruct, isDoctor]);

  const filteredMenuStructure: MenuStructure = useMemo(() => {
    return filterMenu(menuStructure);
  }, [menuStructure]);

  const GetParentKey = useCallback((key: string) => getParentKey(filteredMenuStructure, key), [filteredMenuStructure]);

  const contextValue = useMemo(
    () => ({
      menuStructure,
      filteredMenuStructure,
      getIndexRouteKey: getIndexRouteKey(filteredMenuStructure, true),
      getParentKey: GetParentKey,
      RouteConstruct,
      RouteConstructStrKeys: RouteConstruct as RoutesStructureWithStringKeys,
      toReactRouter: RoutingTransformer(RouteConstruct as RoutesStructureWithStringKeys),
      getAllPaths: () => getAllPaths(RouteConstruct),
    }),
    [menuStructure, filteredMenuStructure, GetParentKey, RouteConstruct]
  );

  if (!auth) return null;

  return <RoutingContext.Provider value={contextValue}>{children}</RoutingContext.Provider>;
};

// Safe version of filterMenu with proper typing
function filterMenu(menu: MenuItem[]): MenuItem[] {
  return menu
    .map((item): MenuItem | null => {
      const children = item.children ? filterMenu(item.children) : undefined;

      const isVisible = item.show === true;
      const hasChildren = Array.isArray(children);
      const hasVisibleChildren = hasChildren && children.length > 0;

      if (isVisible && (!hasChildren || hasVisibleChildren)) {
        return { ...item, children };
      }

      return null;
    })
    .filter((item): item is MenuItem => item !== null);
}
